import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { adminApi } from '../lib/adminAuth';
import {
  Plus, Edit, Trash2, Search, Filter, X, Check, RefreshCw,
  CreditCard, TrendingUp, Clock, CheckCircle, AlertCircle,
  ArrowUpDown, ChevronDown, ChevronUp, Loader2, Download,
  Calendar, Repeat, XCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { SearchableSelect } from '../components/ui/searchable-select';
import { formatCurrency } from '../lib/formatters';
import { getTodayLocal } from '../lib/dateUtils';
import { HeaderSkeleton, MetricCardsSkeleton, TableSkeleton } from './skeletons/Skeletons';

const STATUS_CONFIG = {
  pending: { label: 'Pending', bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', icon: Clock },
  approved: { label: 'Approved', bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', icon: Check },
  paid: { label: 'Paid', bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', icon: CheckCircle },
};

const TYPE_CONFIG = {
  fixed: { label: 'Fixed', bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-200' },
  variable: { label: 'Variable', bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-xl text-xs font-semibold border ${cfg.bg} ${cfg.text} ${cfg.border}`}>
      <Icon className="w-3 h-3" /> {cfg.label}
    </span>
  );
};

const TypeBadge = ({ type }) => {
  const cfg = TYPE_CONFIG[type] || TYPE_CONFIG.variable;
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-xl text-xs font-semibold border ${cfg.bg} ${cfg.text} ${cfg.border}`}>
      {cfg.label}
    </span>
  );
};

// ─── Expense Form Modal ─────────────────────────────────

function ExpenseFormModal({ expense, categories, onSave, onClose }) {
  const [form, setForm] = useState({
    name: '', amount: '', category: '', type: 'variable', date: getTodayLocal(),
    status: 'pending', vendor: '', description: '', payment_method: '', is_recurring: false, recurrence: 'monthly'
  });

  useEffect(() => {
    if (expense) {
      setForm({
        name: expense.name || '',
        amount: expense.amount || '',
        category: expense.category || '',
        type: expense.type || 'variable',
        date: expense.date || '',
        status: expense.status || 'pending',
        vendor: expense.vendor || '',
        description: expense.description || '',
        payment_method: expense.payment_method || '',
        is_recurring: expense.is_recurring || false,
        recurrence: expense.recurrence || 'monthly'
      });
    }
  }, [expense]);

  const handleCategoryChange = (cat) => {
    const catObj = categories.find(c => c.name === cat);
    setForm(prev => ({ ...prev, category: cat, type: catObj?.type || prev.type }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.amount || !form.category || !form.date) {
      toast.error('Please fill all required fields');
      return;
    }
    onSave({ ...form, amount: parseFloat(form.amount) });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="expense-form-modal">
      <div className="bg-white rounded-xl w-full max-w-lg mx-4 shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-white/40">
          <h2 className="text-lg font-bold text-[#1a1a2e]">{expense ? 'Edit Expense' : 'Add New Expense'}</h2>
          <button onClick={onClose} className="p-1 hover:bg-neutral-100 rounded-xl"><X className="w-5 h-5 text-neutral-500" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-neutral-700 mb-1">Expense Name <span className="text-red-500">*</span></label>
              <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Office Rent - Feb 2026" data-testid="expense-name" />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Amount <span className="text-red-500">*</span></label>
              <Input type="number" step="0.01" min="0" value={form.amount} onChange={e => setForm(p => ({ ...p, amount: e.target.value }))} placeholder="0.00" data-testid="expense-amount" />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Date <span className="text-red-500">*</span></label>
              <Input type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} data-testid="expense-date" />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Category <span className="text-red-500">*</span></label>
              <SearchableSelect
                value={form.category}
                onChange={handleCategoryChange}
                placeholder="Select category"
                triggerClassName="h-10 rounded-xl"
                options={[{ value: '', label: 'Select category' }, ...categories.map(c => ({ value: c.name, label: `${c.name} (${c.type})` }))]}
                data-testid="expense-category"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Type</label>
              <div className="flex gap-2 mt-1">
                {['fixed', 'variable'].map(t => (
                  <button key={t} type="button" onClick={() => setForm(p => ({ ...p, type: t }))}
                    className={`flex-1 px-3 py-2 rounded-xl text-sm font-medium border transition-colors ${
                      form.type === t ? (t === 'fixed' ? 'bg-indigo-50 border-indigo-300 text-indigo-700' : 'bg-orange-50 border-orange-300 text-orange-700') : 'bg-white border-white/40 text-neutral-500 hover:bg-white/60'
                    }`} data-testid={`expense-type-${t}`}>
                    {t === 'fixed' ? 'Fixed' : 'Variable'}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Status</label>
              <SearchableSelect
                value={form.status}
                onChange={(v) => setForm(p => ({ ...p, status: v }))}
                triggerClassName="h-10 rounded-xl"
                options={[
                  { value: 'pending', label: 'Pending' },
                  { value: 'approved', label: 'Approved' },
                  { value: 'paid', label: 'Paid' },
                ]}
                data-testid="expense-status"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Vendor</label>
              <Input value={form.vendor} onChange={e => setForm(p => ({ ...p, vendor: e.target.value }))} placeholder="Vendor name" data-testid="expense-vendor" />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Payment Method</label>
              <SearchableSelect
                value={form.payment_method}
                onChange={(v) => setForm(p => ({ ...p, payment_method: v }))}
                placeholder="Select"
                triggerClassName="h-10 rounded-xl"
                options={[
                  { value: '', label: 'Select' },
                  { value: 'bank_transfer', label: 'Bank Transfer' },
                  { value: 'upi', label: 'UPI' },
                  { value: 'credit_card', label: 'Credit Card' },
                  { value: 'cash', label: 'Cash' },
                  { value: 'cheque', label: 'Cheque' },
                ]}
                data-testid="expense-payment-method"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-neutral-700 mb-1">Description</label>
              <textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                rows={2} placeholder="Optional notes..." className="w-full rounded-xl border border-white/40 px-3 py-2 text-sm focus:ring-2 focus:ring-pink-400 focus:border-pink-400"
                data-testid="expense-description" />
            </div>
            <div className="col-span-2 flex items-center gap-4 p-3 bg-white/40 rounded-xl">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.is_recurring} onChange={e => setForm(p => ({ ...p, is_recurring: e.target.checked }))}
                  className="rounded border-white/40 text-pink-600 focus:ring-pink-500" data-testid="expense-recurring" />
                <span className="text-sm font-medium text-neutral-700">Recurring expense</span>
              </label>
              {form.is_recurring && (
                <SearchableSelect
                  value={form.recurrence}
                  onChange={(v) => setForm(p => ({ ...p, recurrence: v }))}
                  triggerClassName="h-8 rounded-xl"
                  options={[
                    { value: 'monthly', label: 'Monthly' },
                    { value: 'quarterly', label: 'Quarterly' },
                    { value: 'annually', label: 'Annually' },
                  ]}
                  data-testid="expense-recurrence"
                />
              )}
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2 border-t border-white/30">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" className="bg-pink-600 hover:bg-pink-700 text-white" data-testid="expense-save-btn">
              {expense ? 'Update Expense' : 'Add Expense'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Main Expenses Component ─────────────────────────────

export default function ExpensesManager() {
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'date', dir: 'desc' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [expRes, catRes, sumRes] = await Promise.all([
        adminApi.get('/api/admin/accounting/expenses'),
        adminApi.get('/api/admin/accounting/expense-categories'),
        adminApi.get('/api/admin/accounting/expenses/summary')
      ]);
      setExpenses(expRes.data.expenses || []);
      setCategories(catRes.data || []);
      setSummary(sumRes.data);
    } catch {
      toast.error('Failed to load expenses');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async (formData) => {
    try {
      if (editingExpense) {
        await adminApi.put(`/api/admin/accounting/expenses/${editingExpense.id}`, formData);
        toast.success('Expense updated');
      } else {
        await adminApi.post('/api/admin/accounting/expenses', formData);
        toast.success('Expense created');
      }
      setShowForm(false);
      setEditingExpense(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save expense');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await adminApi.delete(`/api/admin/accounting/expenses/${id}`);
      toast.success('Expense deleted');
      fetchData();
    } catch {
      toast.error('Failed to delete expense');
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await adminApi.put(`/api/admin/accounting/expenses/${id}`, { status: newStatus });
      toast.success(`Expense marked as ${newStatus}`);
      fetchData();
    } catch {
      toast.error('Failed to update status');
    }
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({ key, dir: prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc' }));
  };

  const activeFilterCount = [filterType, filterCategory, filterStatus, searchTerm].filter(Boolean).length;

  const filteredExpenses = useMemo(() => {
    let items = [...expenses];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      items = items.filter(e => e.name?.toLowerCase().includes(term) || e.vendor?.toLowerCase().includes(term) || e.description?.toLowerCase().includes(term));
    }
    if (filterType) items = items.filter(e => e.type === filterType);
    if (filterCategory) items = items.filter(e => e.category === filterCategory);
    if (filterStatus) items = items.filter(e => e.status === filterStatus);

    items.sort((a, b) => {
      const { key, dir } = sortConfig;
      let aVal = a[key], bVal = b[key];
      if (key === 'amount') { aVal = Number(aVal) || 0; bVal = Number(bVal) || 0; }
      else { aVal = String(aVal || ''); bVal = String(bVal || ''); }
      const cmp = typeof aVal === 'number' ? aVal - bVal : aVal.localeCompare(bVal);
      return dir === 'asc' ? cmp : -cmp;
    });
    return items;
  }, [expenses, searchTerm, filterType, filterCategory, filterStatus, sortConfig]);

  const exportCSV = () => {
    const headers = ['Name', 'Amount', 'Category', 'Type', 'Date', 'Status', 'Vendor', 'Payment Method', 'Recurring', 'Description'];
    const rows = filteredExpenses.map(e => [
      `"${e.name}"`, e.amount, e.category, e.type, e.date, e.status, `"${e.vendor || ''}"`, e.payment_method || '', e.is_recurring ? 'Yes' : 'No', `"${e.description || ''}"`
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url;
    a.download = `expenses_${getTodayLocal()}.csv`;
    a.click(); URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  const SortBtn = ({ label, sortKey }) => (
    <button onClick={() => handleSort(sortKey)} className="flex items-center gap-1 font-semibold text-neutral-600 hover:text-[#1a1a2e] text-xs uppercase tracking-wide group">
      {label}
      <ArrowUpDown className={`w-3 h-3 ${sortConfig.key === sortKey ? 'text-pink-600' : 'text-neutral-300 group-hover:text-neutral-400'}`} />
    </button>
  );

  if (loading) {
    return (
      <div className="space-y-5" data-testid="expenses-manager-loading">
        <HeaderSkeleton />
        <MetricCardsSkeleton count={3} />
        <TableSkeleton rows={6} columns={5} />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="expenses-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-[#1a1a2e]">Expense Management</h2>
          <p className="text-sm text-neutral-500 mt-0.5">Track fixed and variable operating expenses</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchData} className="p-2 text-neutral-500 hover:bg-neutral-100 rounded-xl" title="Refresh" data-testid="expenses-refresh"><RefreshCw className="w-5 h-5" /></button>
          <button onClick={exportCSV} className="p-2 text-neutral-500 hover:bg-neutral-100 rounded-xl" title="Export CSV" data-testid="expenses-export"><Download className="w-5 h-5" /></button>
          <Button onClick={() => { setEditingExpense(null); setShowForm(true); }} className="bg-pink-600 hover:bg-pink-700 text-white" data-testid="add-expense-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Add Expense
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="glass-card p-4">
          <div className="flex items-center gap-2 mb-2"><CreditCard className="w-4 h-4 text-neutral-400" /><span className="text-xs text-neutral-500 font-medium">Total Expenses</span></div>
          <p className="text-2xl font-bold text-[#1a1a2e]">{formatCurrency(summary?.totals?.all || 0)}</p>
          <p className="text-xs text-neutral-400 mt-0.5">{summary?.totals?.count || 0} entries</p>
        </div>
        <div className="glass-card p-4">
          <div className="flex items-center gap-2 mb-2"><div className="w-2 h-2 rounded-full bg-indigo-500"></div><span className="text-xs text-neutral-500 font-medium">Fixed</span></div>
          <p className="text-2xl font-bold text-indigo-700">{formatCurrency(summary?.totals?.fixed || 0)}</p>
          <p className="text-xs text-neutral-400 mt-0.5">Recurring & predictable</p>
        </div>
        <div className="glass-card p-4">
          <div className="flex items-center gap-2 mb-2"><div className="w-2 h-2 rounded-full bg-orange-500"></div><span className="text-xs text-neutral-500 font-medium">Variable</span></div>
          <p className="text-2xl font-bold text-orange-700">{formatCurrency(summary?.totals?.variable || 0)}</p>
          <p className="text-xs text-neutral-400 mt-0.5">Ad-hoc & fluctuating</p>
        </div>
        <div className="glass-card p-4">
          <div className="flex items-center gap-2 mb-2"><TrendingUp className="w-4 h-4 text-neutral-400" /><span className="text-xs text-neutral-500 font-medium">Categories</span></div>
          <p className="text-2xl font-bold text-[#1a1a2e]">{summary?.category_breakdown?.length || 0}</p>
          <p className="text-xs text-neutral-400 mt-0.5">Active categories</p>
        </div>
      </div>

      {/* Category Breakdown */}
      {summary?.category_breakdown?.length > 0 && (
        <div className="glass-card p-4">
          <h3 className="text-sm font-semibold text-neutral-700 mb-3">Spend by Category</h3>
          <div className="space-y-2">
            {summary.category_breakdown.map((cat) => {
              const pct = summary.totals.all > 0 ? (cat.total / summary.totals.all * 100) : 0;
              return (
                <div key={cat.category} className="flex items-center gap-3">
                  <span className="text-xs font-medium text-neutral-600 w-44 truncate">{cat.category}</span>
                  <div className="flex-1 bg-neutral-100 rounded-full h-2">
                    <div className={`h-2 rounded-full transition-all ${cat.type === 'fixed' ? 'bg-indigo-400' : 'bg-orange-400'}`} style={{ width: `${Math.max(pct, 2)}%` }} />
                  </div>
                  <span className="text-xs font-semibold text-neutral-700 w-28 text-right">{formatCurrency(cat.total)}</span>
                  <span className="text-[10px] text-neutral-400 w-10 text-right">{pct.toFixed(0)}%</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Filter Bar */}
      <div className="glass-card p-3 flex flex-wrap gap-3 items-center">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <input type="text" placeholder="Search expenses..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            className="pl-9 pr-4 py-2 text-sm border border-white/40 rounded-xl w-48 focus:ring-2 focus:ring-pink-400 focus:border-pink-400" data-testid="expenses-search" />
        </div>
        <SearchableSelect
          value={filterType} onChange={setFilterType} placeholder="All Types"
          triggerClassName="h-9 min-w-[140px] text-sm rounded-xl"
          options={[{ value: '', label: 'All Types' }, { value: 'fixed', label: 'Fixed' }, { value: 'variable', label: 'Variable' }]}
          data-testid="expenses-filter-type"
        />
        <SearchableSelect
          value={filterCategory} onChange={setFilterCategory} placeholder="All Categories"
          triggerClassName="h-9 min-w-[160px] text-sm rounded-xl"
          options={[{ value: '', label: 'All Categories' }, ...categories.map(c => ({ value: c.name, label: c.name }))]}
          data-testid="expenses-filter-category"
        />
        <SearchableSelect
          value={filterStatus} onChange={setFilterStatus} placeholder="All Statuses"
          triggerClassName="h-9 min-w-[140px] text-sm rounded-xl"
          options={[{ value: '', label: 'All Statuses' }, { value: 'pending', label: 'Pending' }, { value: 'approved', label: 'Approved' }, { value: 'paid', label: 'Paid' }]}
          data-testid="expenses-filter-status"
        />
        {activeFilterCount > 0 && (
          <button onClick={() => { setSearchTerm(''); setFilterType(''); setFilterCategory(''); setFilterStatus(''); }}
            className="inline-flex items-center gap-1 px-3 py-2 text-xs font-medium text-red-600 hover:bg-red-50 rounded-xl" data-testid="expenses-clear-filters">
            <XCircle className="w-3.5 h-3.5" /> Clear ({activeFilterCount})
          </button>
        )}
        <span className="ml-auto text-xs text-neutral-400">{filteredExpenses.length} of {expenses.length} expenses</span>
      </div>

      {/* Expenses Table */}
      <div className="soft-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="expenses-table">
            <thead className="bg-white/40 border-b border-white/40">
              <tr>
                <th className="px-4 py-3 text-left"><SortBtn label="Date" sortKey="date" /></th>
                <th className="px-4 py-3 text-left"><SortBtn label="Name" sortKey="name" /></th>
                <th className="px-4 py-3 text-left"><SortBtn label="Category" sortKey="category" /></th>
                <th className="px-4 py-3 text-center">Type</th>
                <th className="px-4 py-3 text-right"><SortBtn label="Amount" sortKey="amount" /></th>
                <th className="px-4 py-3 text-left">Vendor</th>
                <th className="px-4 py-3 text-center"><SortBtn label="Status" sortKey="status" /></th>
                <th className="px-4 py-3 text-center w-24">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/30">
              {filteredExpenses.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-neutral-400">
                    <CreditCard className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p className="font-medium">No expenses found</p>
                    <Button onClick={() => { setEditingExpense(null); setShowForm(true); }} className="mt-3 bg-pink-600 hover:bg-pink-700 text-white text-sm">
                      <Plus className="w-3.5 h-3.5 mr-1" /> Add First Expense
                    </Button>
                  </td>
                </tr>
              ) : filteredExpenses.map(exp => (
                <tr key={exp.id} className="hover:bg-neutral-50 transition-colors" data-testid={`expense-row-${exp.id}`}>
                  <td className="px-4 py-3 text-neutral-600 whitespace-nowrap">{exp.date}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-[#1a1a2e]">{exp.name}</span>
                      {exp.is_recurring && (
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-violet-50 text-violet-600 border border-violet-200" title={`Recurs ${exp.recurrence}`}>
                          <Repeat className="w-2.5 h-2.5" /> {exp.recurrence}
                        </span>
                      )}
                    </div>
                    {exp.description && <p className="text-xs text-neutral-400 mt-0.5 truncate max-w-xs">{exp.description}</p>}
                  </td>
                  <td className="px-4 py-3 text-neutral-600 text-xs">{exp.category}</td>
                  <td className="px-4 py-3 text-center"><TypeBadge type={exp.type} /></td>
                  <td className="px-4 py-3 text-right font-semibold text-[#1a1a2e]">{formatCurrency(exp.amount)}</td>
                  <td className="px-4 py-3 text-neutral-500 text-xs">{exp.vendor || '-'}</td>
                  <td className="px-4 py-3 text-center">
                    <div className="relative group inline-block">
                      <StatusBadge status={exp.status} />
                      <div className="absolute left-1/2 -translate-x-1/2 top-full mt-1 hidden group-hover:flex glass-card rounded-xl shadow-lg p-1 z-10 gap-1">
                        {['pending', 'approved', 'paid'].filter(s => s !== exp.status).map(s => (
                          <button key={s} onClick={() => handleStatusChange(exp.id, s)}
                            className="px-2 py-1 text-xs rounded hover:bg-neutral-100 whitespace-nowrap capitalize">{s}</button>
                        ))}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-1">
                      <button onClick={() => { setEditingExpense(exp); setShowForm(true); }}
                        className="p-1.5 text-neutral-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl" title="Edit" data-testid={`edit-expense-${exp.id}`}>
                        <Edit className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => handleDelete(exp.id)}
                        className="p-1.5 text-neutral-400 hover:text-red-600 hover:bg-red-50 rounded-xl" title="Delete" data-testid={`delete-expense-${exp.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <ExpenseFormModal
          expense={editingExpense}
          categories={categories}
          onSave={handleSave}
          onClose={() => { setShowForm(false); setEditingExpense(null); }}
        />
      )}
    </div>
  );
}
